// Image FILES from the operator's computer (drag-and-drop or the file picker) ->
// the JPEG the upload contract requires (api.ts: the presigned PUT is image/jpeg
// only, and the server stores it as <ulid>/original.jpg).
//
// Every file is decoded and re-encoded through a canvas — even a JPEG — which
// bakes in the EXIF orientation, strips metadata (phone GPS never reaches the
// PUBLIC bucket), flattens transparency onto white, and caps the long edge at
// 4096px (the camera's ideal resolution: keeps Gemini's inline payload small and
// stays inside iOS Safari's canvas-area limit). The browser decodes JPEG / PNG /
// GIF / WebP / AVIF / BMP natively everywhere, and HEIC + TIFF in Safari; the
// HEIC and TIFF decoders are lazy-loaded only when the browser can't (Chrome,
// Firefox), so they never weigh on the main bundle.

export type ImageFormat = "jpeg" | "png" | "gif" | "webp" | "bmp" | "tiff" | "heic" | "avif";

export const FORMAT_LABEL: Record<ImageFormat, string> = {
  jpeg: "JPEG",
  png: "PNG",
  gif: "GIF",
  webp: "WebP",
  bmp: "BMP",
  tiff: "TIFF",
  heic: "HEIC",
  avif: "AVIF",
};

/** For the file input's `accept`: MIME types plus extensions (macOS often leaves .heic untyped). */
export const ACCEPT_IMAGE_FILES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/bmp",
  "image/tiff",
  "image/heic",
  "image/heif",
  ".jpg",
  ".jpeg",
  ".jfif",
  ".png",
  ".gif",
  ".webp",
  ".avif",
  ".bmp",
  ".tif",
  ".tiff",
  ".heic",
  ".heif",
].join(",");

export const SUPPORTED_FORMATS_TEXT = "JPEG, PNG, HEIC, WebP, GIF, TIFF, BMP or AVIF";

const MAX_EDGE = 4096;
const PREVIEW_EDGE = 320;
const JPEG_QUALITY = 0.92; // same as the camera capture (camera.ts)
const PREVIEW_QUALITY = 0.8;

// ISO-BMFF `ftyp` brands of HEIF stills/sequences (AVIF shares the container).
const HEIF_BRANDS = new Set([
  "heic", "heix", "hevc", "hevx", "heim", "heis", "hevm", "hevs", "mif1", "mif2", "msf1",
]);

/**
 * Identify a supported image by its magic bytes (never trust the name or the
 * MIME type: macOS reports "" for HEIC, and anything can be renamed .jpg).
 * Returns null for anything we don't accept (PDF, SVG, RAW, video, …).
 */
export async function sniffImageFormat(file: Blob): Promise<ImageFormat | null> {
  const b = new Uint8Array(await file.slice(0, 64).arrayBuffer());
  const ascii = (at: number, len: number) =>
    String.fromCharCode(...b.subarray(at, Math.min(at + len, b.length)));

  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "jpeg";
  if (ascii(0, 8) === "\x89PNG\r\n\x1a\n") return "png";
  if (ascii(0, 6) === "GIF87a" || ascii(0, 6) === "GIF89a") return "gif";
  if (ascii(0, 4) === "RIFF" && ascii(8, 4) === "WEBP") return "webp";
  if (ascii(0, 4) === "II*\0" || ascii(0, 4) === "MM\0*") return "tiff";
  if (ascii(0, 2) === "BM") return "bmp";
  if (ascii(4, 4) === "ftyp") {
    // Box size (big-endian) bounds the major brand + compatible-brand list.
    const boxEnd = Math.min(((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0, b.length);
    const brands = [ascii(8, 4)];
    for (let at = 16; at + 4 <= boxEnd; at += 4) brands.push(ascii(at, 4));
    // AVIF files list "mif1" too, so check for AVIF first.
    if (brands.some((br) => br === "avif" || br === "avis")) return "avif";
    if (brands.some((br) => HEIF_BRANDS.has(br))) return "heic";
  }
  return null;
}

export interface PreparedImage {
  /** Upright, metadata-free JPEG, long edge ≤ 4096px — what gets uploaded. */
  jpeg: Blob;
  /** Small JPEG for the Upload pane's thumbnail strip. */
  preview: Blob;
}

/**
 * Decode an image file and re-encode it as the upload JPEG (+ a small preview).
 * Throws an Error with an operator-readable message if the file can't be read.
 */
export async function prepareImageFile(file: Blob, format: ImageFormat): Promise<PreparedImage> {
  const decoded = await decode(file, format);
  try {
    const full = drawScaled(decoded.source, decoded.width, decoded.height, MAX_EDGE);
    try {
      const small = drawScaled(full, full.width, full.height, PREVIEW_EDGE);
      try {
        const [jpeg, preview] = await Promise.all([
          encodeJpeg(full, JPEG_QUALITY),
          encodeJpeg(small, PREVIEW_QUALITY),
        ]);
        return { jpeg, preview };
      } finally {
        releaseCanvas(small);
      }
    } finally {
      releaseCanvas(full);
    }
  } finally {
    decoded.release();
  }
}

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function decode(file: Blob, format: ImageFormat): Promise<Decoded> {
  try {
    return await decodeNative(file);
  } catch {
    // Not decodable by this browser — fall through to a bundled decoder below.
  }
  try {
    if (format === "heic") return fromBitmap(await decodeHeic(file));
    if (format === "tiff") return fromBitmap(await decodeTiff(file));
  } catch (err) {
    console.warn(`${FORMAT_LABEL[format]} decode failed`, err);
    throw new Error(`Couldn't read this ${FORMAT_LABEL[format]} file — it may be damaged.`);
  }
  throw new Error(
    `This browser can't open ${FORMAT_LABEL[format]} images — try Safari, or save it as a JPEG.`,
  );
}

/**
 * The browser's own decoder, via <img>: drawing an <img> applies its EXIF
 * orientation in every current browser (createImageBitmap's handling varies).
 */
async function decodeNative(file: Blob): Promise<Decoded> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
  return {
    source: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    release: () => URL.revokeObjectURL(url),
  };
}

function fromBitmap(bitmap: ImageBitmap): Decoded {
  return {
    source: bitmap,
    width: bitmap.width,
    height: bitmap.height,
    release: () => bitmap.close(),
  };
}

/** HEIC/HEIF via libheif (WASM, runs in a worker); applies the HEIF rotation/mirror. */
async function decodeHeic(file: Blob): Promise<ImageBitmap> {
  const { heicTo } = await import("heic-to/csp");
  return heicTo({ blob: file, type: "bitmap" });
}

/** TIFF via UTIF.js: decode the largest page (skips embedded reduced-size previews). */
async function decodeTiff(file: Blob): Promise<ImageBitmap> {
  const mod = await import("utif2");
  // UTIF.js is CommonJS: depending on the interop its API is on the namespace or on `default`.
  const UTIF = (mod as unknown as { default?: typeof mod }).default ?? mod;
  const buf = await file.arrayBuffer();
  // t256/t257 = ImageWidth/ImageLength, readable before the pixels are decoded.
  const pixels = (ifd: Record<string, unknown>) =>
    ((ifd.t256 as number[] | undefined)?.[0] ?? 0) * ((ifd.t257 as number[] | undefined)?.[0] ?? 0);
  const pages = UTIF.decode(buf);
  let page = pages[0];
  for (const ifd of pages) if (pixels(ifd) > pixels(page)) page = ifd;
  if (!page) throw new Error("TIFF has no image");
  UTIF.decodeImage(buf, page);
  const rgba = UTIF.toRGBA8(page);
  const data = new ImageData(
    // Same bytes, no copy (toRGBA8 allocates a plain ArrayBuffer, never a shared one).
    new Uint8ClampedArray(rgba.buffer as ArrayBuffer, rgba.byteOffset, rgba.byteLength),
    page.width,
    page.height,
  );
  return createImageBitmap(data);
}

/** Draw `source` into a new canvas, downscaled so its long edge is ≤ maxEdge (never upscaled). */
function drawScaled(
  source: CanvasImageSource,
  width: number,
  height: number,
  maxEdge: number,
): HTMLCanvasElement {
  if (!width || !height) throw new Error("This image is empty.");
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Couldn't prepare the image (no canvas).");
  // JPEG has no alpha: flatten transparent PNG/GIF/WebP onto white, not black.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function encodeJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Couldn't convert the image to JPEG."))),
      "image/jpeg",
      quality,
    );
  });
}

/** Zero the backing store now (iOS Safari otherwise holds canvas memory until GC). */
function releaseCanvas(canvas: HTMLCanvasElement) {
  canvas.width = 0;
  canvas.height = 0;
}
